/**
 * PartiesTab — fiches clients ou fournisseurs (tenue de livres d'un dossier · fournisseurs du cabinet) : recherche, totaux, création / modification.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, GitMerge, Plus, Search, Sparkles } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, Empty, Pill, accLabel, errMsg, inputCls, money, useGlBase } from "../comptabilite/_shared";
import { PARTY_CFG } from "./_books";
import { SortTh, useSort } from "@/components/ListTools";
import PartyDialog from "./PartyDialog";
import PartyCleanupDialog from "./PartyCleanupDialog";

export default function PartiesTab({ kind, accounts, pathFor }) {
  const base = useGlBase();
  const navigate = useNavigate();
  const cfg = PARTY_CFG[kind];
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(null); // null | "new" | party
  const [cleanup, setCleanup] = useState(null);
  const [showCleanup, setShowCleanup] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get(`${base}/${cfg.path}`, { params: { q: q || undefined } }); setData(r.data); }
    catch (e) { toast.error(errMsg(e, `Chargement des ${cfg.plural.toLowerCase()} impossible`)); }
  }, [base, cfg, q]);
  useEffect(() => { load(); }, [load]);

  const loadCleanup = useCallback(async () => {
    try { const r = await api.get(`${base}/${cfg.path}/cleanup`); setCleanup(r.data); }
    catch { setCleanup(null); }
  }, [base, cfg]);
  useEffect(() => { loadCleanup(); }, [loadCleanup]);

  const changed = () => { load(); loadCleanup(); };
  const { sort, onSort, apply } = useSort("number", "asc");
  const items = apply(data?.items || [], { number: (p) => (/^\d+$/.test(p.number || "") ? Number(p.number) : p.number ? p.number : "\uffff" + p.name), nb: (p) => p.totals.nb, ytd: (p) => p.totals.ytd, balance: (p) => p.totals.balance, last: (p) => p.totals.last_date || "" });
  const year = new Date().getFullYear();
  const nbDup = cleanup?.groups?.length || 0;
  const nbUp = cleanup?.uppercase?.length || 0;

  return (
    <div className="space-y-4" data-testid={`parties-tab-${kind}`}>
      {(nbDup > 0 || nbUp > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 flex items-center gap-2 flex-wrap text-xs text-amber-900" data-testid="parties-cleanup-banner">
          <Sparkles className="w-4 h-4 text-amber-600 shrink-0" />
          <span><strong>Ménage suggéré</strong> : {nbDup > 0 && <span data-testid="parties-cleanup-dup-count">{nbDup} groupe(s) de fiches en double probable</span>}{nbDup > 0 && nbUp > 0 && " · "}{nbUp > 0 && <span data-testid="parties-cleanup-upper-count">{nbUp} nom(s) tout en majuscules</span>}</span>
          <Btn tone="amber" icon={GitMerge} onClick={() => setShowCleanup(true)} testId="parties-cleanup-btn" className="ml-auto">Nettoyer</Btn>
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Rechercher un ${cfg.label.toLowerCase()}…`} className={`${inputCls} pl-9 w-64`} data-testid="parties-search" />
          </div>
          {data && <span className="text-xs text-slate-500">{items.length} {cfg.plural.toLowerCase()} · {cfg.balanceLabel.toLowerCase()} <strong className={data.total_balance > 0 ? (kind === "customer" ? "text-sky-700" : "text-rose-700") : "text-slate-700"} data-testid="parties-total-balance">{money(data.total_balance)}</strong></span>}
        </div>
        <div className="flex gap-2">
          <Btn tone="slate" icon={Clock} onClick={() => navigate(`${pathFor("rapports")}?report=${kind}-aging`)} testId="parties-aging-link">Âge des comptes</Btn>
          <Btn icon={Plus} onClick={() => setDialog("new")} testId="parties-new-btn">Nouveau {cfg.label.toLowerCase()}</Btn>
        </div>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <SortTh label={`N° · ${cfg.label}`} k="number" sort={sort} onSort={onSort} className="text-left px-3 py-2" testId="parties-sort-number" />
              <th className="text-left px-3 py-2 hidden lg:table-cell">Compte par défaut</th>
              <SortTh label="Factures" k="nb" sort={sort} onSort={onSort} className="text-right px-3 py-2 w-20" align="right" testId="parties-sort-nb" />
              <SortTh label={`${kind === "customer" ? "Ventes" : "Achats"} ${year}`} k="ytd" sort={sort} onSort={onSort} className="text-right px-3 py-2 w-32" align="right" testId="parties-sort-ytd" />
              <SortTh label={cfg.balanceLabel} k="balance" sort={sort} onSort={onSort} className="text-right px-3 py-2 w-32" align="right" testId="parties-sort-balance" />
              <SortTh label="Dernière" k="last" sort={sort} onSort={onSort} className="text-left px-3 py-2 w-28 hidden md:table-cell" testId="parties-sort-last" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.map((p) => (
              <tr key={p.id} className={`hover:bg-teal-50/40 cursor-pointer ${p.is_active === false ? "text-slate-400" : ""}`} onClick={() => setDialog(p)} data-testid={`party-row-${p.id}`}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2 flex-wrap">{p.number && <span className="font-mono text-xs text-teal-700">{p.number}</span>}<span className="font-medium">{p.name}</span>{p.is_active === false && <Pill>Inactif</Pill>}</div>
                  {(p.contact_name || p.email || p.phone) && <div className="text-[11px] text-slate-400">{[p.contact_name, p.email, p.phone].filter(Boolean).join(" · ")}</div>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500 hidden lg:table-cell">{p.default_account_id ? accLabel(accounts, p.default_account_id) : "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{p.totals.nb}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">{money(p.totals.ytd)}</td>
                <td className={`px-3 py-2 text-right font-mono tabular-nums ${p.totals.balance > 0 ? (kind === "customer" ? "text-sky-700 font-semibold" : "text-rose-700 font-semibold") : ""}`} data-testid={`party-balance-${p.id}`}>{p.totals.balance ? money(p.totals.balance) : "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-500 hidden md:table-cell">{p.totals.last_date || "—"}</td>
              </tr>
            ))}
            {data && !items.length && <tr><td colSpan={6}><Empty icon={cfg.icon} text={`Aucun ${cfg.label.toLowerCase()}.`} testId="parties-empty"><Btn icon={Plus} onClick={() => setDialog("new")} testId="parties-empty-new-btn">Créer un {cfg.label.toLowerCase()}</Btn></Empty></td></tr>}
          </tbody>
        </table>
      </div>

      {dialog && <PartyDialog kind={kind} party={dialog === "new" ? null : dialog} accounts={accounts} pathFor={pathFor} nextNumber={data?.next_number} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); changed(); }} onChanged={changed} />}
      {showCleanup && <PartyCleanupDialog kind={kind} data={cleanup} onClose={() => setShowCleanup(false)} onChanged={changed} />}
    </div>
  );
}
